import { useApi } from '@backstage/core-plugin-api';
import { useCallback, useEffect, useState } from 'react';
import { userSettingsApiRef } from '@mercedes-benz/user-settings-react';
import { datadogRum } from '@datadog/browser-rum';

const TECH_INSIGHTS_PREFERENCES = 'tech-insights-preferences';
const INSIGHTS_APPS_USAGE_FEATURE = 'insights-apps-usage';
const MAX_HISTORY = 5;

/**
 * Represents a single usage record of an app.
 */
export type AppUsage = {
  /** Unique ID of the app */
  appId: string;

  /** ISO string timestamp when the app was used */
  timestamp: string;
};

/**
 * Custom hook to track and persist the most recently used apps
 * for the Tech Insights platform, stored via `userSettingsApi`.
 *
 * It limits the retained app history to the latest 5 entries.
 *
 * @returns {{
 *   usedApps: Record<string, AppUsage>;
 *   sortedApps: AppUsage[];
 *   onAppUsage: (appId: string) => Promise<void>;
 * }}
 */
export const useRecentApps = () => {
  const userSettingsApi = useApi(userSettingsApiRef);
  const [usedApps, setUsedApps] = useState<Record<string, AppUsage>>({});

  /**
   * Records the usage of an app and updates the internal state,
   * retaining only the latest 5 entries sorted by recency.
   *
   * @param appId - The unique identifier of the app being used
   */
  const onAppUsage = useCallback(
    async (appId: string) => {
      const now = new Date().toISOString();

      const updatedApps = {
        ...usedApps,
        [appId]: {
          appId,
          timestamp: now,
        },
      };

      const trimmedArray = Object.values(updatedApps)
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        )
        .slice(0, MAX_HISTORY);

      const trimmedRecord: Record<string, AppUsage> = {};
      for (const usage of trimmedArray) {
        trimmedRecord[usage.appId] = usage;
      }

      setUsedApps(trimmedRecord);

      // Persists the current usage record to the user settings.
      await userSettingsApi.set({
        bucket: TECH_INSIGHTS_PREFERENCES,
        key: INSIGHTS_APPS_USAGE_FEATURE,
        value: JSON.stringify(trimmedRecord),
      });
    },
    [usedApps, userSettingsApi],
  );

  /**
   * Loads the previously stored app usage data from settings
   * when the hook is first mounted.
   */
  useEffect(() => {
    const loadUsedApps = async () => {
      try {
        const preference = await userSettingsApi.get({
          bucket: TECH_INSIGHTS_PREFERENCES,
          key: INSIGHTS_APPS_USAGE_FEATURE,
        });

        if (preference?.value) {
          const parsed: Record<string, AppUsage> = JSON.parse(
            preference.value as string,
          );
          setUsedApps(parsed);
        }
      } catch (error) {
        datadogRum.addError(error, {
          hook: 'useRecentApps',
          message: 'Failed to load recent apps from settings',
        });
      }
    };

    loadUsedApps();
  }, [userSettingsApi]);

  /**
   * Sorted array of recent apps, most recent first.
   */
  const sortedApps = Object.values(usedApps).sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  return { usedApps, sortedApps, onAppUsage };
};
